import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import MiniPlanogram from './MiniPlanogram';
import MonthlyUnitsBars, { hasAbsentMonth } from './MonthlyUnitsBars';

const HISTORY_MONTHS = 6; // months of sales a hovered lane shows under the planogram

/**
 * A machine's planogram beside the replacement rows, the same wherever replacements are picked (Top Productos by
 * machine, "Probar en N máquinas", the lanes picked on the Machines planogram): the going-out product's lanes
 * (`chosenLaneIds` marked, a click picks or unpicks them through `onToggleLane`), a click on any other lane makes its
 * product the choice through `onPick` (only `pickedLaneId` is marked), and the lane under the mouse shows what its
 * product sold from that lane over the last months (and when it was not there), every lane on the same scale.
 */
const PlanogramSidePanel = ({ title, clientId, storeId, highlightProductId, pickedLaneId, chosenLaneIds, onToggleLane, onPick, onReady }) => {
  const { t, i18n } = useTranslation();
  const locale = { es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES';
  const [hoveredLane, setHoveredLane] = useState(null);
  useEffect(() => { setHoveredLane(null); }, [storeId, highlightProductId]);
  return (
    <>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: colors.text.primary, mb: 0.5 }}>{title}</Typography>
      <MiniPlanogram
        key={`${storeId}-${highlightProductId}`}
        clientId={clientId}
        storeId={storeId}
        highlightProductId={highlightProductId}
        pickedLaneId={pickedLaneId}
        chosenLaneIds={chosenLaneIds}
        cellHeight={34}
        months={HISTORY_MONTHS}
        onHoverLane={setHoveredLane}
        onReady={onReady}
        onToggleLane={onToggleLane}
        onPick={onPick}
      />
      {/* Fixed height, so moving over the lanes never moves the popover */}
      <Box sx={{ height: 112, mt: 1, pt: 1, borderTop: `1px solid ${colors.border.light}` }}>
        {hoveredLane ? (
          <>
            <Typography noWrap sx={{ fontSize: '11px', fontWeight: 600, color: colors.text.primary }}>
              {hoveredLane.name}
              <Box component="span" sx={{ fontWeight: 400, color: colors.text.secondary, ml: 0.75 }}>
                {t('productMachines_laneMonthlyUnits', { lane: hoveredLane.selectionId })}
              </Box>
            </Typography>
            {hoveredLane.loading ? (
              <Typography sx={{ fontSize: '11px', color: colors.text.secondary, mt: 1 }}>{t('productMachines_laneLoading')}</Typography>
            ) : (
              <>
                <MonthlyUnitsBars
                  months={hoveredLane.months}
                  series={hoveredLane.series}
                  days={hoveredLane.days}
                  snapshotDays={hoveredLane.snapshotDays}
                  max={hoveredLane.scaleMax}
                  locale={locale}
                />
                {hasAbsentMonth(hoveredLane.series, hoveredLane.days) && (
                  <Typography sx={{ fontSize: '10px', color: colors.text.muted, mt: 0.25 }}>{t('productMachines_laneLegend')}</Typography>
                )}
              </>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: '11px', color: colors.text.secondary }}>
            {t('productMachines_hoverHint', { count: HISTORY_MONTHS })}
          </Typography>
        )}
      </Box>
    </>
  );
};

export default PlanogramSidePanel;
